/**
 * Admin · Program schedules
 *
 * Top-level page listing every published program with its credential type
 * and cohort schedules summarised. Clicking a row opens the existing
 * ProgramScheduleDialog where admin can add/edit start + end dates per
 * admission term.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    CalendarDays, BookOpen, Search, Loader2, ChevronRight, Filter,
} from "lucide-react";
import ProgramScheduleDialog from "@/components/admin/ProgramScheduleDialog";

function fmtError(e, fb) { return e?.response?.data?.detail || e?.message || fb; }

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }); }
    catch { return iso; }
}

export default function ProgramSchedulesAdmin() {
    const [programs, setPrograms] = useState([]);
    const [schedules, setSchedules] = useState({}); // {program_id: [entries]}
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [credFilter, setCredFilter] = useState("all");
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // Pull every non-archived program (published or draft) so admins
            // can also schedule a program they're about to publish.
            const { data } = await api.get("/programs", { params: { include_archived: true } });
            const list = Array.isArray(data) ? data : (data.items || []);
            // Only include programs that aren't deleted — these are
            // the ones a prospective applicant could actually be assigned to.
            const visible = list.filter((p) => !p.is_deleted);
            setPrograms(visible);
            // Bulk-load schedules in parallel — small N, single page render
            const pairs = await Promise.all(
                visible.map(async (p) => {
                    try {
                        const { data: s } = await api.get(`/admin/programs/${p.id}/schedule`);
                        return [p.id, s.items || []];
                    } catch { return [p.id, []]; }
                }),
            );
            const map = {};
            pairs.forEach(([pid, items]) => { map[pid] = items; });
            setSchedules(map);
        } catch (e) {
            toast.error(fmtError(e, "Could not load programs"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        let out = programs;
        if (credFilter !== "all") {
            out = out.filter((p) => (p.credential_type || "").toLowerCase() === credFilter);
        }
        if (q.trim()) {
            const needle = q.trim().toLowerCase();
            out = out.filter((p) => p.title?.toLowerCase().includes(needle));
        }
        // Sort: programs WITH schedules last (so unscheduled ones bubble up)
        return [...out].sort((a, b) => {
            const an = schedules[a.id]?.length || 0;
            const bn = schedules[b.id]?.length || 0;
            if (an === 0 && bn !== 0) return -1;
            if (bn === 0 && an !== 0) return 1;
            return (a.title || "").localeCompare(b.title || "");
        });
    }, [programs, schedules, q, credFilter]);

    const stats = useMemo(() => {
        const totalCohorts = Object.values(schedules).reduce((a, b) => a + (b?.length || 0), 0);
        const unscheduled = programs.filter((p) => !(schedules[p.id]?.length)).length;
        return { totalCohorts, unscheduled };
    }, [programs, schedules]);

    const onDialogClose = (saved) => {
        setEditing(null);
        if (saved) load();
    };

    return (
        <div className="max-w-7xl mx-auto px-6 py-10 pb-24" data-testid="admin-program-schedules">
            <Link to="/admin" className="text-sm text-muted-foreground hover:text-primary">
                ← Back
            </Link>
            <div className="mt-2 mb-8">
                <div className="inline-flex items-center gap-1.5 chip chip-primary">
                    <CalendarDays className="h-3 w-3" />
                    Scheduling
                </div>
                <h1 className="font-display text-3xl md:text-4xl text-foreground mt-3 tracking-tight">
                    Program schedules
                </h1>
                <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">
                    Set cohort start and end dates per admission term, per program.
                    Each program can have multiple cohort dates within a term.
                    Applicants see these dates when picking their start date on the
                    public Apply Now form.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <Card className="p-3 bg-[#FBF5E8] border-[#E1DAC5]">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">
                        Programs
                    </div>
                    <div className="font-display text-3xl mt-1 text-[#134B30]">{programs.length}</div>
                </Card>
                <Card className="p-3 bg-emerald-50 border-emerald-200">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">
                        Scheduled cohorts
                    </div>
                    <div className="font-display text-3xl mt-1 text-[#134B30]">{stats.totalCohorts}</div>
                </Card>
                <Card className="p-3 bg-amber-50 border-amber-200">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">
                        Needs scheduling
                    </div>
                    <div className="font-display text-3xl mt-1 text-[#134B30]">{stats.unscheduled}</div>
                </Card>
            </div>

            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div className="relative w-full sm:w-96">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search programs…"
                        className="pl-9"
                        data-testid="schedules-search"
                    />
                </div>
                <div className="inline-flex items-center gap-1 rounded-md bg-muted/50 p-1">
                    <Filter className="h-3 w-3 text-muted-foreground ml-2" />
                    {[
                        { v: "all", label: "All" },
                        { v: "diploma", label: "Diploma" },
                        { v: "certificate", label: "Certificate" },
                    ].map((opt) => (
                        <button
                            key={opt.v}
                            type="button"
                            onClick={() => setCredFilter(opt.v)}
                            className={`px-3 py-1 text-xs rounded ${credFilter === opt.v ? "bg-[#134B30] text-white" : "text-muted-foreground hover:text-foreground"}`}
                            data-testid={`schedules-filter-${opt.v}`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            <Card className="surface-card border-border">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-12 flex items-center justify-center text-muted-foreground">
                            <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-16 text-center text-sm text-muted-foreground">
                            <BookOpen className="h-10 w-10 mx-auto text-[#C79520]/60 mb-3" />
                            No programs match your filters.
                        </div>
                    ) : (
                        <ul className="divide-y divide-border" data-testid="schedules-list">
                            {filtered.map((p) => {
                                const items = schedules[p.id] || [];
                                return (
                                    <li
                                        key={p.id}
                                        className="px-4 py-3 hover:bg-muted/20 transition cursor-pointer flex items-center gap-4"
                                        onClick={() => setEditing(p)}
                                        data-testid={`schedules-row-${p.id}`}
                                    >
                                        <div className="flex-shrink-0 w-10 h-10 rounded bg-[#134B30]/10 flex items-center justify-center text-[#134B30]">
                                            <BookOpen className="h-4 w-4" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-medium text-sm truncate">{p.title}</span>
                                                <Badge variant="outline" className="text-[10px] capitalize">
                                                    {p.credential_type || "Uncategorised"}
                                                </Badge>
                                                {!p.is_published && (
                                                    <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-900">
                                                        Draft
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-3 flex-wrap">
                                                {items.length === 0 ? (
                                                    <span className="italic text-amber-700">No cohort dates yet</span>
                                                ) : (
                                                    <>
                                                        <span className="inline-flex items-center gap-1">
                                                            <CalendarDays className="h-3 w-3" />
                                                            {items.length} cohort{items.length === 1 ? "" : "s"}
                                                        </span>
                                                        <span className="truncate">
                                                            Next: {fmtDate(items[0].start_date)} → {fmtDate(items[0].end_date)} · {items[0].term}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={(e) => { e.stopPropagation(); setEditing(p); }}
                                            data-testid={`schedules-edit-${p.id}`}
                                            className="gap-1.5"
                                        >
                                            <CalendarDays className="h-3 w-3" />
                                            {items.length ? "Edit dates" : "Set dates"}
                                            <ChevronRight className="h-3 w-3" />
                                        </Button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </CardContent>
            </Card>

            {editing && (
                <ProgramScheduleDialog
                    program={editing}
                    onClose={onDialogClose}
                />
            )}
        </div>
    );
}
